import { type ReactNode, useState, useEffect } from 'react'
import { usePage, router } from '@inertiajs/react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  CalendarDays,
  Globe,
  Settings,
  KeyRound,
  LogOut,
  Menu,
  X,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  MessageSquare,
  Mail,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface SharedProps {
  flash: { alert?: string; notice?: string }
  [key: string]: unknown
}

export interface AppShellProps {
  locale: string
  logoUrl: string | null
  accountName: string
  userEmail: string
  canEditAccount: boolean
  hideNav?: boolean
  wideLayout?: boolean
  fullWidth?: boolean
  urls: {
    events: string
    accountSettings: string
    changePassword: string
    logout: string
    whatsappTemplates?: string
    invitations?: string
  }
  translations: {
    events: string
    accountSettings: string
    changePassword: string
    logout: string
    logoutConfirm: string
    logoutConfirmBody: string
    cancel: string
    whatsappTemplates?: string
    invitations?: string
  }
  children: ReactNode
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function AppShell({
  locale,
  logoUrl,
  accountName,
  userEmail,
  canEditAccount,
  hideNav,
  wideLayout,
  fullWidth,
  urls,
  translations: t,
  children,
}: AppShellProps) {
  const { flash } = usePage<SharedProps>().props
  const isRtl = locale === 'ar'

  const [mobileOpen, setMobileOpen] = useState(false)
  const [logoutOpen, setLogoutOpen] = useState(false)
  const [flashVisible, setFlashVisible] = useState(true)

  // Auto-dismiss flash after 5s
  useEffect(() => {
    if (flash?.alert || flash?.notice) {
      setFlashVisible(true)
      const timer = setTimeout(() => setFlashVisible(false), 5000)
      return () => clearTimeout(timer)
    }
  }, [flash?.alert, flash?.notice])

  const otherLocale = locale === 'ar' ? 'en' : 'ar'
  const otherLocaleLabel = locale === 'ar' ? 'English' : 'عربي'

  const switchLocale = () => {
    const url = new URL(window.location.href)
    const parts = url.pathname.split('/')
    if (parts[1] === 'en' || parts[1] === 'ar') parts[1] = otherLocale
    url.pathname = parts.join('/')
    window.location.href = url.toString()
  }

  const handleLogout = () => router.delete(urls.logout)

  const initials = userEmail ? userEmail.substring(0, 2).toUpperCase() : '??'

  return (
    <div className={`min-h-screen bg-[#FFFFFF] ${hideNav ? '' : 'pt-11'}`} dir={isRtl ? 'rtl' : 'ltr'}>
      {/* ─── Notion-style thin topbar ─── */}
      {!hideNav && <header className="fixed top-0 left-0 right-0 w-full z-40 flex h-11 items-center justify-between border-b border-[#E8E8E5] bg-white/80 backdrop-blur-sm px-3 sm:px-4">
        {/* Left: breadcrumb-style nav */}
        <div className="flex items-center gap-1 min-w-0">
          <a
            href={urls.events}
            className="flex items-center gap-1.5 rounded-[4px] px-1.5 py-0.5 text-sm text-[#91918E] hover:bg-[#F1F1EF] transition-colors shrink-0"
          >
            {logoUrl ? (
              <img src={logoUrl} alt={accountName} className="h-5 w-5 rounded object-contain" />
            ) : (
              <span className="flex h-5 w-5 items-center justify-center rounded bg-gradient-to-br from-[#2DD4BF] to-[#8B5CF6] text-[9px] font-bold text-white">
                {accountName.charAt(0).toUpperCase()}
              </span>
            )}
            <span className="font-medium text-[#37352F] max-w-[120px] truncate hidden sm:inline">
              {accountName}
            </span>
          </a>

          <ChevronRight className="h-3 w-3 text-[#D3D3D0] shrink-0 rtl:rotate-180" />

          <a
            href={urls.events}
            className="flex items-center gap-1.5 rounded-[4px] px-1.5 py-0.5 text-sm font-medium text-[#37352F] hover:bg-[#F1F1EF] transition-colors"
          >
            <CalendarDays className="h-3.5 w-3.5 text-[#91918E]" />
            <span className="hidden lg:inline">{t.events}</span>
          </a>
          {urls.whatsappTemplates && (
            <a
              href={urls.whatsappTemplates}
              className="flex items-center gap-1.5 rounded-[4px] px-1.5 py-0.5 text-sm font-medium text-[#37352F] hover:bg-[#F1F1EF] transition-colors"
            >
              <MessageSquare className="h-3.5 w-3.5 text-[#91918E]" />
              <span className="hidden lg:inline">{t.whatsappTemplates}</span>
            </a>
          )}
          {urls.invitations && (
            <a
              href={urls.invitations}
              className="flex items-center gap-1.5 rounded-[4px] px-1.5 py-0.5 text-sm font-medium text-[#37352F] hover:bg-[#F1F1EF] transition-colors"
            >
              <Mail className="h-3.5 w-3.5 text-[#91918E]" />
              <span className="hidden lg:inline">{t.invitations}</span>
            </a>
          )}
        </div>

        {/* Right: actions */}
        <div className="flex items-center">
          {/* Language toggle */}
          <button
            onClick={switchLocale}
            className="hidden sm:flex items-center gap-1 rounded-[4px] px-2 py-1 text-xs text-[#91918E] hover:bg-[#F1F1EF] transition-colors cursor-pointer"
          >
            <Globe className="h-3.5 w-3.5" />
            {otherLocaleLabel}
          </button>

          {/* User dropdown */}
          <div className="hidden sm:block">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1.5 rounded-[4px] px-1.5 py-1 hover:bg-[#F1F1EF] cursor-pointer outline-none transition-colors">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[#E16B5A] text-[9px] font-bold text-white">
                  {initials}
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRtl ? 'start' : 'end'} className="w-60" sideOffset={8}>
                {/* User info header */}
                <div className="px-3 py-2.5 border-b border-[#E8E8E5]">
                  <p className="text-sm font-medium text-[#37352F] truncate">{userEmail}</p>
                  <p className="text-xs text-[#91918E] mt-0.5">{accountName}</p>
                </div>
                <div className="py-1">
                  {canEditAccount && (
                    <DropdownMenuItem
                      render={<a href={urls.accountSettings} />}
                      className="cursor-pointer text-[#37352F] gap-2 px-3 py-1.5"
                    >
                      <Settings className="h-4 w-4 text-[#91918E]" />
                      {t.accountSettings}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    render={<a href={urls.changePassword} />}
                    className="cursor-pointer text-[#37352F] gap-2 px-3 py-1.5"
                  >
                    <KeyRound className="h-4 w-4 text-[#91918E]" />
                    {t.changePassword}
                  </DropdownMenuItem>
                </div>
                <DropdownMenuSeparator className="bg-[#E8E8E5]" />
                <div className="py-1">
                  <DropdownMenuItem
                    onClick={() => setLogoutOpen(true)}
                    className="cursor-pointer text-[#EB5757] gap-2 px-3 py-1.5 focus:text-[#EB5757]"
                  >
                    <LogOut className="h-4 w-4" />
                    {t.logout}
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile menu */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="sm:hidden flex items-center justify-center h-7 w-7 rounded-[4px] hover:bg-[#F1F1EF] text-[#91918E] cursor-pointer"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </header>}

      {/* Mobile dropdown */}
      {!hideNav && mobileOpen && (
        <div className="sm:hidden border-b border-[#E8E8E5] bg-white px-2 py-2 space-y-0.5 animate-in slide-in-from-top-1 duration-150">
          <a href={urls.events} className="flex items-center gap-2 rounded-[4px] px-3 py-2 text-sm text-[#37352F] hover:bg-[#F1F1EF]">
            <CalendarDays className="h-4 w-4 text-[#91918E]" />
            {t.events}
          </a>
          {urls.whatsappTemplates && (
            <a href={urls.whatsappTemplates} className="flex items-center gap-2 rounded-[4px] px-3 py-2 text-sm text-[#37352F] hover:bg-[#F1F1EF]">
              <MessageSquare className="h-4 w-4 text-[#91918E]" />
              {t.whatsappTemplates}
            </a>
          )}
          {urls.invitations && (
            <a href={urls.invitations} className="flex items-center gap-2 rounded-[4px] px-3 py-2 text-sm text-[#37352F] hover:bg-[#F1F1EF]">
              <Mail className="h-4 w-4 text-[#91918E]" />
              {t.invitations}
            </a>
          )}
          <button onClick={switchLocale} className="flex w-full items-center gap-2 rounded-[4px] px-3 py-2 text-sm text-[#37352F] hover:bg-[#F1F1EF] cursor-pointer">
            <Globe className="h-4 w-4 text-[#91918E]" />
            {otherLocaleLabel}
          </button>
          {canEditAccount && (
            <a href={urls.accountSettings} className="flex items-center gap-2 rounded-[4px] px-3 py-2 text-sm text-[#37352F] hover:bg-[#F1F1EF]">
              <Settings className="h-4 w-4 text-[#91918E]" />
              {t.accountSettings}
            </a>
          )}
          <a href={urls.changePassword} className="flex items-center gap-2 rounded-[4px] px-3 py-2 text-sm text-[#37352F] hover:bg-[#F1F1EF]">
            <KeyRound className="h-4 w-4 text-[#91918E]" />
            {t.changePassword}
          </a>
          <button
            onClick={() => { setMobileOpen(false); setLogoutOpen(true) }}
            className="flex w-full items-center gap-2 rounded-[4px] px-3 py-2 text-sm text-[#EB5757] hover:bg-red-50 cursor-pointer"
          >
            <LogOut className="h-4 w-4" />
            {t.logout}
          </button>
        </div>
      )}

      {/* ─── Flash toasts ─── */}
      {flashVisible && (flash?.alert || flash?.notice) && (
        <div className="fixed bottom-4 ltr:right-4 rtl:left-4 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          {flash?.alert && (
            <div className="flex items-center gap-2 rounded-lg bg-[#FFF3F3] border border-[#FDD8D8] px-4 py-3 text-sm text-[#EB5757] shadow-lg shadow-black/5 max-w-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="line-clamp-2">{flash.alert}</span>
              <button onClick={() => setFlashVisible(false)} className="ml-auto shrink-0 hover:opacity-70 cursor-pointer"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}
          {flash?.notice && (
            <div className="flex items-center gap-2 rounded-lg bg-[#F0FFF4] border border-[#C6F6D5] px-4 py-3 text-sm text-[#276749] shadow-lg shadow-black/5 max-w-sm">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span className="line-clamp-2">{flash.notice}</span>
              <button onClick={() => setFlashVisible(false)} className="ml-auto shrink-0 hover:opacity-70 cursor-pointer"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}
        </div>
      )}

      {/* ─── Page content ─── */}
      <main className={[
        fullWidth ? 'w-full' : wideLayout ? 'mx-auto max-w-[90rem] px-4 sm:px-6' : 'mx-auto max-w-5xl px-4 sm:px-8 lg:px-12',
        hideNav ? 'pb-0' : 'pb-16',
      ].join(' ')}>
        {children}
      </main>

      {/* ─── Logout dialog ─── */}
      <Dialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-[#37352F]">{t.logoutConfirm}</DialogTitle>
            <DialogDescription className="text-[#91918E]">{t.logoutConfirmBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setLogoutOpen(false)} className="border-[#E8E8E5] text-[#37352F] hover:bg-[#F1F1EF]">
              {t.cancel}
            </Button>
            <Button onClick={handleLogout} className="bg-[#EB5757] hover:bg-[#E04848] text-white">
              <LogOut className="h-4 w-4 ltr:mr-1.5 rtl:ml-1.5" />
              {t.logout}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
